import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { Persona } from 'src/app/models/personas.interface';
import { ManagePersonasData } from '../manage-personas-data';
import { ManagePersonasViewComponent } from '../view/manage-personas-view.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PersonaService } from 'src/app/services/persona.service';
import { UploadDialogComponent } from './upload-dialog/upload-dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DriftValidators } from 'src/app/shared/validators';
import { FileMetadata } from 'src/app/models/collection.interface';
import { AlertModalComponent } from '../../../alert-modal/alert-modal.component';

enum ManagePersonasEditSteps {
  CREATE_TITLE,
  EDIT_PERSONA
}

@Component({
  selector: 'app-manage-personas-edit',
  templateUrl: './manage-personas-edit.component.html',
  styleUrls: ['./manage-personas-edit.component.scss']
})
export class ManagePersonasEditComponent implements OnInit {
  @Input() selectedPersona!: Persona;
  @Input() personaTitles!: string[];
  @ViewChild('fileInputPersona') fileInputPersona!: ElementRef;
  readonly ManagePersonasEditSteps = ManagePersonasEditSteps;

  editPersonaFormGroup!: FormGroup;
  filesToUpload: FileList | null = null;
  uploadDialog = inject(MatDialog);
  modelPersonaResponse = '';
  personaGenInProgress = false;
  isNewPersona = false;
  personaLogFilename = '';
  fullPersonaDescResponse = '';
  personaGenFinished = false;
  personaGenLoading = false;
  typingIndex = 0; // Index for typewriter effect
  typingTimeout?: NodeJS.Timeout; // Timeout ID for typing effect
  currentStep!: ManagePersonasEditSteps;
  isUploading = true;
  showLoadingText = false;

  currentUploadNames = new Set<string>();

  constructor(
    public dialog: MatDialog,
    private managePersonasData: ManagePersonasData,
    private formBuilder: FormBuilder,
    private personaService: PersonaService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.applyPeronaDefault();
    this.setupPersonaForm();
  }

  onPersonaUploadFiles(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      const duplicateFileList = [];
      const uploadList = [];

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (!this.selectedPersona.exampleDocuments?.find(doc => doc.file_id === file.name)) {
          uploadList.push(file);
          this.selectedPersona.exampleDocuments?.push({
            file_id: file.name,
            collection_id: '',
            processing_stage: 'started',
            original_document_uri: file.webkitRelativePath,
            s3_output_path: `personas/${this.selectedPersona.id}/${file.name.replace('.pdf', '.txt')}`
          });
        } else {
          duplicateFileList.push(file.name);
        }
      }

      this.uploadDocs(uploadList);

      // this.generatePersonaFromFileList(this.selectedPersona);

      if (duplicateFileList.length > 0) {
        const list = duplicateFileList.join(', ');
        this.snackBar.open(`The following documents have already been uploaded: ${list}.`, 'Close', { duration: 6000 });
      }
    }
  }

  uploadDocs(files: File[]) {
    this.showLoadingText = true;

    if (!this.selectedPersona.id) {
      console.error('Upload failed, persona needs to be saved before files can be uploaded.');
      this.snackBar.open('Save this persona before uploading files.', 'Close');
      return;
    }
    const id = this.selectedPersona.id;
    const fileArr = Array.from(files);

    this.currentUploadNames = new Set(fileArr.map(f => f.name));

    this.personaService.convertPDFtoTxtAndUpload(id, files, 'personas').subscribe({
      next: () => {
        this.snackBar.open('Upload successful', 'Close', { duration: 5000 });
        this.isUploading = false;
        this.showLoadingText = false;
        this.currentUploadNames.clear(); // stop showing indeterminate progress bars
      },
      error: (error: HttpErrorResponse) => {
        this.snackBar.open('Error uploading files', 'Close');
        console.error('Upload failed', error);
        this.showLoadingText = false;
        this.currentUploadNames.clear(); // stop showing indeterminate progress bars

        const fileListArr = Array.from(files);
        this.selectedPersona.exampleDocuments = this.selectedPersona.exampleDocuments?.filter(
          doc => !fileListArr.find(a => a.name === doc.file_id)
        );
      }
    });
  }

  onPersonaFileRemove(doc: FileMetadata) {
    if (!this.selectedPersona.id) {
      console.error('Removing the file failed, missing persona id.');
      this.snackBar.open('Error removing the example document.', 'Close', { duration: 5000 });
      return;
    }
    const id = this.selectedPersona.id;
    this.personaService.deleteUploadedDocument(id, doc.file_id).subscribe(() => {
      const index = this.selectedPersona.exampleDocuments.findIndex(data => data.file_id === doc.file_id);
      this.selectedPersona.exampleDocuments.splice(index, 1);
    });
  }

  enableEdit(description: string) {
    this.personaGenInProgress = false;
    this.editPersonaFormGroup.patchValue({ description: description });
    this.editPersonaFormGroup.get('description')?.enable();
  }

  generatePersonaFromFileList(persona: Persona) {
    if (persona.exampleDocuments.length > 0) {
      this.personaGenInProgress = true;
      this.editPersonaFormGroup
        .get('description')
        ?.setValue('Generating description. Please allow up to 2 minutes ...');
      this.editPersonaFormGroup.get('description')?.disable();

      this.personaService.personaLog(persona).subscribe({
        next: response => {
          this.personaLogFilename = persona.log_filename = response.filename;
          this.personaService.generatePersona(persona).subscribe({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            next: (response: { description: string }) => {
              this.pollGeneratePersonaResponse(); // start polling for the result
            },
            error: error => {
              if (error.status === 503) {
                console.warn('503 received - backend may still be processing. Starting polling.', 'Close');
              } else {
                this.snackBar.open('Error generating persona description. Please try again.', 'Close');
                console.error('Error generating persona description:', error);
                this.enableEdit('');
                this.personaGenInProgress = false;
                return; // dont poll if real error occurs
              }
              this.pollGeneratePersonaResponse(); // still poll even on 503
            }
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error logging persona:', error);
          this.snackBar.open('Error logging persona. Please try again.', 'Close');
          this.personaGenInProgress = false;
        }
      });
    }
  }

  pollGeneratePersonaResponse() {
    const maxAttempts = 60; // Increase this if more time is needed
    const delay = 2000; // Poll every 2 seconds
    let attempts = 0;

    const poll = () => {
      attempts++;

      if (attempts > maxAttempts) {
        this.openAlert('Failed', 'Response timed out.');
        this.personaGenInProgress = false;
        return;
      }

      this.personaService.pollGenPersonaResult(this.personaLogFilename).subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (response: any) => {
          if (response.status === 200) {
            if (response.body.complete === 'true') {
              this.fullPersonaDescResponse = response.body.description;
              this.enableEdit(this.fullPersonaDescResponse);
              this.editPersonaFormGroup.updateValueAndValidity();
            } else {
              this.fullPersonaDescResponse = response.body.response;
              setTimeout(poll, delay);
              if (this.personaGenLoading && this.fullPersonaDescResponse.length) {
                this.personaGenLoading = false;
              }
              this.typingIndex = this.modelPersonaResponse.length;
              this.startTypingEffect();
              setTimeout(poll, delay); // Retry after a delay
            }
          } else {
            setTimeout(poll, delay); // Retry after a delay
          }
        },
        error: (error: HttpErrorResponse) => {
          this.openAlert('Failed', 'Error while polling.');
          console.error('Error while polling', error);
          this.personaGenInProgress = false;
        }
      });
    };

    poll();
  }

  startTypingEffect() {
    const typingSpeed = 4;

    if (this.fullPersonaDescResponse || !this.fullPersonaDescResponse.length) {
      console.warn('Typing effect skipped - no fullPersonaDescResponse available.');
      return;
    }

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout); // Clear any existing timeout to prevent overlapping typing
    }

    const typeCharacter = () => {
      if (this.typingIndex < this.fullPersonaDescResponse.length) {
        this.modelPersonaResponse += this.fullPersonaDescResponse.charAt(this.typingIndex);
        this.typingIndex++;
        this.typingTimeout = setTimeout(typeCharacter, typingSpeed);
      }
    };

    typeCharacter();
  }

  onSave() {
    this.selectedPersona.name = this.editPersonaFormGroup.get('title')?.getRawValue();
    this.selectedPersona.summary = this.editPersonaFormGroup.get('summary')?.getRawValue();
    this.selectedPersona.description = this.editPersonaFormGroup.get('description')?.getRawValue();

    if (this.isNewPersona) {
      this.personaService.createPersona(this.selectedPersona).subscribe({
        next: (persona: Persona) => {
          console.info(`Create persona: ${JSON.stringify(persona)}`);
          this.isNewPersona = false;
          this.selectedPersona.id = persona.id;
          this.editPersonaFormGroup.get('description')?.enable();
          this.currentStep = ManagePersonasEditSteps.EDIT_PERSONA;
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open('Error creating a new persona. Please try again.', 'Close');
          console.error('Error creating Persona.', error);
        }
      });
    } else {
      this.personaService.updatePersona(this.selectedPersona).subscribe({
        next: (persona: Persona) => {
          console.info(`Update persona: ${JSON.stringify(persona)}`);
          this.showView();
        },
        error: (error: HttpErrorResponse) => {
          this.snackBar.open('Error updating persona. Please try again.', 'Close');
          console.error('Error updating Persona.', error);
        }
      });
    }
  }

  showView() {
    this.managePersonasData.emitViewChange({ componentName: ManagePersonasViewComponent.name });
  }

  showNextStep() {
    this.managePersonasData.emitViewChange({
      componentName: ManagePersonasEditComponent.name,
      persona: this.selectedPersona
    });
  }

  showUploadModule() {
    const dialog = this.uploadDialog.open(UploadDialogComponent, {
      width: '600px'
    });
    dialog.afterClosed().subscribe(result => {
      if (result) {
        this.fileInputPersona.nativeElement.click();
      }
    });
  }

  private applyPeronaDefault() {
    if (!this.selectedPersona) {
      this.isNewPersona = true;
      this.selectedPersona = {
        name: '',
        summary: '',
        description: '',
        exampleDocuments: []
      };
      this.currentStep = ManagePersonasEditSteps.CREATE_TITLE;
    } else if (!this.selectedPersona.exampleDocuments) {
      this.currentStep = ManagePersonasEditSteps.EDIT_PERSONA;
      this.selectedPersona.exampleDocuments = [];
    }
  }

  private setupPersonaForm() {
    this.editPersonaFormGroup = this.formBuilder.group({
      title: new FormControl(this.selectedPersona.name, [
        Validators.required,
        Validators.maxLength(100),
        DriftValidators.unique(this.personaTitles)
      ]),
      summary: new FormControl(this.selectedPersona.summary, [Validators.maxLength(500)]),
      description: new FormControl(this.selectedPersona.description, [
        Validators.required,
        Validators.maxLength(5000),
        Validators.minLength(1)
      ])
    });
    if (this.currentStep === ManagePersonasEditSteps.CREATE_TITLE)
      this.editPersonaFormGroup.get('description')?.disable();
  }

  openAlert(title: string, message: string): void {
    this.dialog.open(AlertModalComponent, {
      data: {
        title: title,
        message: message
      }
    });
  }

  showDocLoadingText() {
    return this.showLoadingText;
  }

  // returns true if the specific doc is being uploaded
  // when true show indeterminate progress bar for that doc
  // when false show determinate progress bar for that doc so prior uploads don't appear to re-upload
  isUploadingDoc(doc: { file_id?: string; name?: string }): boolean {
    const key = doc?.file_id ?? doc?.name ?? '';
    return this.showLoadingText && !!key && this.currentUploadNames.has(key);
  }
}
